import { useEffect, useRef, useState, useCallback } from "react";
import {
  getMetodosPago,
  getMetodoPagoById,
  crearMetodoPago,
  actualizarMetodoPago,
} from "../../../services/usuarios_tienda/metodos_pago";
import PaymentFormModal from "./PaymentFormModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import "./PaymentMethodsPage.css";

function PaymentMethodsPage() {
  const [metodos, setMetodos] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [loading, setLoading] = useState(false);
  const [loadingEditMetodo, setLoadingEditMetodo] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedMetodo, setSelectedMetodo] = useState(null);

  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const getErrorMessage = (error, fallback = "Ocurrió un error inesperado.") => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  };

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const cargarMetodos = useCallback(
    async (currentPage = 1) => {
      try {
        setLoading(true);

        const response = await getMetodosPago({
          page: currentPage,
          limit: pageSize,
        });

        setMetodos(Array.isArray(response?.data) ? response.data : []);
        setPagination(
          response?.pagination || {
            page: currentPage,
            limit: pageSize,
            total: 0,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          }
        );
      } catch (error) {
        console.error("Error cargando métodos de pago:", error);
        showAlert(
          "error",
          getErrorMessage(error, "No se pudieron cargar los métodos de pago.")
        );
        setMetodos([]);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, showAlert]
  );

  useEffect(() => {
    cargarMetodos(page);
  }, [page, cargarMetodos]);

  const openCreateModal = () => {
    setSelectedMetodo(null);
    setShowModal(true);
  };

  const openEditModal = async (metodo) => {
    if (!metodo?.tienda_metodo_pago_id) {
      showAlert("error", "No se pudo identificar el método de pago.");
      return;
    }

    setLoadingEditMetodo(true);

    try {
      const response = await getMetodoPagoById(metodo.tienda_metodo_pago_id);
      const detalle = response?.data || null;

      if (!detalle) {
        throw new Error("No se pudo obtener el detalle del método de pago.");
      }

      setSelectedMetodo(detalle);
      setShowModal(true);
    } catch (error) {
      console.error("Error cargando método de pago:", error);
      showAlert(
        "error",
        getErrorMessage(error, "No se pudo cargar el método de pago.")
      );
    } finally {
      setLoadingEditMetodo(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      const response = await crearMetodoPago(data);

      showAlert(
        "success",
        response?.message || "Método de pago creado correctamente."
      );

      setShowModal(false);
      setSelectedMetodo(null);

      if (page !== 1) {
        setPage(1);
      } else {
        await cargarMetodos(1);
      }
    } catch (error) {
      console.error("Create payment method failed:", error);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const response = await actualizarMetodoPago(id, data);

      showAlert(
        "success",
        response?.message || "Método de pago actualizado correctamente."
      );

      setShowModal(false);
      setSelectedMetodo(null);

      await cargarMetodos(page);
    } catch (error) {
      console.error("Update payment method failed:", error);
      throw error;
    }
  };

  const nextPage = () => {
    if (pagination.hasNextPage) setPage((p) => p + 1);
  };

  const prevPage = () => {
    if (pagination.hasPreviousPage) setPage((p) => p - 1);
  };

  const goToPage = (n) => {
    const total = pagination.totalPages || 1;
    if (n < 1 || n > total || n === pagination.page || loading) return;
    setPage(n);
  };

  const visiblePages = (() => {
    const total = pagination.totalPages || 1;
    const current = pagination.page || 1;
    const count = 3;
    if (total <= count) return Array.from({ length: total }, (_, i) => i + 1);
    let s = Math.max(1, current - Math.floor(count / 2));
    let e = s + count - 1;
    if (e > total) {
      e = total;
      s = e - count + 1;
    }
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  })();

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("es-GT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const getEstadoBadge = (estado) => {
    const activo = Number(estado) === 1;

    return (
      <span className={activo ? "status active" : "status inactive"}>
        {activo ? "Activo" : "Inactivo"}
      </span>
    );
  };

  const getComprobanteBadge = (requiereComprobante) => {
    const requiere = Number(requiereComprobante) === 1;

    return (
      <span className={requiere ? "status pending" : "status processing"}>
        {requiere ? "Sí requiere" : "No requiere"}
      </span>
    );
  };

  return (
    <div className="tienda-page-scope">
      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="header">
        <div className="methods-header__row">
          <h2 className="title">Métodos de pago</h2>

          {hasPerm("create_payment_methods") && (
            <button type="button" className="methods-btn-create" onClick={openCreateModal}>
              Crear método
            </button>
          )}
        </div>

        <p className="page-lead">
          Configura las formas de pago disponibles en tu tienda y el orden en que se muestran.
        </p>
      </div>

      <div className="table-card">
        <div className="table-card-scroll">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre visible</th>
              <th>Comprobante</th>
              <th>Orden</th>
              <th>Estado</th>
              <th>Fecha creado</th>
              <th style={{ textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="no-data">
                  Cargando métodos de pago...
                </td>
              </tr>
            ) : metodos.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  No se encontraron métodos de pago
                </td>
              </tr>
            ) : (
              metodos.map((m) => (
                <tr key={m.tienda_metodo_pago_id}>
                  <td>{m.tienda_metodo_pago_id}</td>
                  <td>{m.nombre_visible || "-"}</td>
                  <td>{getComprobanteBadge(m.requiere_comprobante)}</td>
                  <td>{m.orden ?? "-"}</td>
                  <td>{getEstadoBadge(m.estado)}</td>
                  <td>{formatDate(m.fecha_creado)}</td>

                  <td style={{ textAlign: "right" }}>
                      {hasPerm("update_payment_methods") && (
                        <span
                          className="action-icon edit"
                          onClick={() => !loadingEditMetodo && openEditModal(m)}
                          title={
                            loadingEditMetodo
                              ? "Cargando método..."
                              : "Editar método de pago"
                          }
                          style={
                            loadingEditMetodo
                              ? { opacity: 0.45, pointerEvents: "none" }
                              : undefined
                          }
                        >
                          {IconEdit}
                        </span>
                      )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {showModal && (
        <PaymentFormModal
          isOpen={showModal}
          metodoData={selectedMetodo}
          onClose={() => {
            setShowModal(false);
            setSelectedMetodo(null);
          }}
          onSubmit={(data) =>
            selectedMetodo?.tienda_metodo_pago_id
              ? handleUpdate(selectedMetodo.tienda_metodo_pago_id, data)
              : handleCreate(data)
          }
        />
      )}

      {(pagination.total > 0 || metodos.length > 0) && (
        <div className="pagination">
          <div className="pagination__center">
            <div className="pagination__nav" aria-label="Paginación de métodos de pago">
              <button
                type="button"
                className="pagination__arrow"
                onClick={prevPage}
                disabled={!pagination.hasPreviousPage || loading}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <div className="pagination__pages">
                {visiblePages.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`pagination__page${n === pagination.page ? " is-active" : ""}`}
                    onClick={() => goToPage(n)}
                    aria-label={`Ir a página ${n}`}
                    aria-current={n === pagination.page ? "page" : undefined}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="pagination__arrow"
                onClick={nextPage}
                disabled={!pagination.hasNextPage || loading}
                aria-label="Página siguiente"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentMethodsPage;